/**
 * Recruiter-friendly CV download filenames.
 * Prefer Name_Surname_CV.pdf (or Name_Surname_Role.pdf) — no template IDs or timestamps.
 */

/**
 * Sanitize one filename fragment (name or title).
 * @param {string} value
 * @param {number} [maxLen=60]
 * @returns {string}
 */
export function sanitizeFilenamePart(value, maxLen = 60) {
  if (!value || typeof value !== 'string') return '';
  return value
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^\p{L}\p{N}._-]+/gu, '')
    .replace(/_+/g, '_')
    .replace(/^[._-]+|[._-]+$/g, '')
    .slice(0, maxLen);
}

/**
 * @param {object} [options]
 * @param {string} [options.name]
 * @param {string} [options.title]
 * @param {string} [options.extension='pdf']
 * @returns {string}
 */
export function buildCvDownloadFilename({ name, title, extension = 'pdf' } = {}) {
  const person = sanitizeFilenamePart(name) || 'CV';
  const role = sanitizeFilenamePart(title, 40);
  const skipRoles = new Set([
    'cv',
    'curriculum',
    'currículum',
    'resume',
    'curriculum_vitae',
    'professional',
    'profesional',
    'professional_candidate',
    'candidate',
  ]);

  let base;
  if (role && !skipRoles.has(role.toLowerCase())) {
    base = `${person}_${role}`;
  } else if (['cv', 'nombre', 'name'].includes(person.toLowerCase())) {
    base = 'CV';
  } else if (person.toLowerCase().endsWith('_cv') || person.toLowerCase() === 'cv') {
    base = person;
  } else {
    base = `${person}_CV`;
  }

  const ext = String(extension || 'pdf').replace(/^\./, '').toLowerCase() || 'pdf';
  return `${base}.${ext}`;
}

/**
 * Keep-look downloads: Name_Surname_CV.ext — never the AI job title.
 * Titles get invented or truncated (Intern → Inter…).
 *
 * @param {object} [options]
 * @param {string} [options.name]
 * @param {string} [options.originalFileName]
 * @param {string} [options.extension='docx']
 * @returns {string}
 */
export function buildKeepLookDownloadFilename({
  name,
  originalFileName,
  extension = 'docx',
} = {}) {
  const fromOriginal = sanitizeFilenamePart(
    String(originalFileName || '')
      .replace(/^original_\d+_/, '')
      .replace(/\.(pdf|docx|doc)$/i, ''),
    80
  );
  const person = sanitizeFilenamePart(name);
  const usable =
    person && !['cv', 'nombre', 'name'].includes(person.toLowerCase())
      ? person
      : fromOriginal;
  return buildCvDownloadFilename({ name: usable, title: '', extension });
}

/**
 * Pull name/title from nested analysis / editable payloads.
 * @param {object} [analysisData]
 * @param {string} [extension='pdf']
 * @returns {string}
 */
export function filenameFromAnalysisData(analysisData, extension = 'pdf') {
  const data = analysisData && typeof analysisData === 'object' ? analysisData : {};
  const buckets = [
    data,
    data.editableContent || {},
    data.final_design || {},
    data.extracted_and_improved || {},
  ];
  let name = '';
  let title = '';
  for (const bucket of buckets) {
    if (!bucket || typeof bucket !== 'object') continue;
    if (!name && bucket.name) name = String(bucket.name);
    if (!title && bucket.title) title = String(bucket.title);
    if (name && title) break;
  }
  return buildCvDownloadFilename({ name, title, extension });
}
